import type { FC } from "react";

import classNames from "classnames";
import DomPurify from "isomorphic-dompurify";

import { Button } from "@jti/core-components/components/Button";
import { Container } from "@jti/core-components/components/Container";

import { BlogItem } from "../BlogListing/BlogItem/BlogItem";
import type { Items } from "../ReassuranceBanner/ReassuranceBanner";
import { ReassuranceBanner } from "../ReassuranceBanner/ReassuranceBanner";

import styles from "./MultiTab.module.scss";

const { sanitize } = DomPurify;

export type MultiTabItem = {
  variant?: "extended" | "buttons" | "blogArticles";
  index?: number;
  image?: string;
  title?: string;
  subtitleBlog?: string;
  description?: string;
  anchor?: string;
};

export type MultiTabProps = {
  multiTabVariant?: MultiTabItem["variant"];
  title: string;
  bottomDescription?: string;
  ctaText?: string;
  ctaHref?: string;
  tabList?: MultiTabItem[];
};

export const MultiTab: FC<MultiTabProps> = ({
  title,
  bottomDescription,
  tabList,
  multiTabVariant,
  ctaText,
  ctaHref,
}: MultiTabProps) => {
  const variantExtended = multiTabVariant === "extended";
  const variantBlogArticles = multiTabVariant === "blogArticles";
  const variantButtons = multiTabVariant === "buttons";

  const ctaSection = ctaText && ctaHref && (
    <div className={styles.ctaSection}>
      <Button href={ctaHref} variant="text-muted">
        {ctaText}
      </Button>
    </div>
  );

  // Render ReassuranceBanner for buttons and extended variants
  if (tabList && (variantButtons || variantExtended)) {
    const reassuranceItems: Items = tabList.map((item, index) => ({
      id: `tab-${index}`,
      title: item.title || "",
      description: item.description || "",
      customIcon: item.image ? (
        <div className={styles.customIconWrapper}>
          <img src={item.image} alt={item.title || ""} />
        </div>
      ) : undefined,
    }));

    return (
      <div className={classNames(styles.container, { [styles.extendedVariant]: variantExtended })}>
        <ReassuranceBanner
          title={title}
          items={reassuranceItems}
          bottomText={variantExtended ? bottomDescription : undefined}
        />
        {ctaSection}
      </div>
    );
  }

  // Render BlogItem for blogArticles variant
  if (variantBlogArticles) {
    return (
      <div className={styles.container}>
        <Container>
          <h2 className={styles.title} dangerouslySetInnerHTML={{ __html: sanitize(title) }} />
          <div className={styles.tabContainer}>
            {tabList?.map((item) => (
              <BlogItem
                key={item.title}
                title={item.title || ""}
                subtitle={item.subtitleBlog || ""}
                description={item.description}
                image={
                  item.image
                    ? {
                        smallReference: item.image,
                        mediumReference: item.image,
                        largeReference: item.image,
                        altText: item.title || "",
                      }
                    : null
                }
                isFeatured={false}
                isAvailable={true}
                isLink={false}
              />
            ))}
          </div>
          {bottomDescription && (
            <div className={styles.bottomText} data-testid="multiTab__subtitle">
              <p>{bottomDescription}</p>
            </div>
          )}
        </Container>
        {ctaSection}
      </div>
    );
  }

  return null;
};
